import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BlockIcon from '@mui/icons-material/Block';
import {
  Progress,
  ErrorPanel,
  EmptyState,
  Link,
} from '@backstage/core-components';
import { CampaignCellStatus } from '@internal/backstage-plugin-bidone-backend';
import {
  useEntityCampaigns,
  useSetCheckCompletion,
} from '../../hooks/useCampaigns';
import { StateLabel } from './stateLabel';

const CheckStatusIcon = ({ status }: { status: CampaignCellStatus }) => {
  switch (status) {
    case 'pass':
      return (
        <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
      );
    case 'fail':
      return <CancelIcon fontSize="small" sx={{ color: 'error.main' }} />;
    case 'exempt':
      return (
        <Tooltip title="Exempt">
          <BlockIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </Tooltip>
      );
    default:
      return (
        <RadioButtonUncheckedIcon
          fontSize="small"
          sx={{ color: 'text.disabled' }}
        />
      );
  }
};

// Full Campaigns tab for a Component entity page: every active campaign that
// targets this component, with its per-check status. Manual checks the owner
// can attest right here.
export const EntityCampaignsTab = () => {
  const { entity } = useEntity();
  const ref = stringifyEntityRef(entity);
  const { data, isFetching, error } = useEntityCampaigns(ref);
  const setCompletion = useSetCheckCompletion();

  if (error) return <ErrorPanel error={error as Error} />;
  if (!data && isFetching) return <Progress />;

  const campaigns = data ?? [];
  if (campaigns.length === 0) {
    return (
      <EmptyState
        missing="info"
        title="No active campaigns"
        description="This component isn't targeted by any active campaign."
      />
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      {campaigns.map(campaign => (
        <Card key={campaign.campaignId}>
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
            >
              <Typography variant="h6">
                <Link to={`/campaigns/${campaign.campaignId}`}>
                  {campaign.name}
                </Link>
              </Typography>
              <StateLabel state={campaign.state} />
              {campaign.targetDate && (
                <Typography variant="caption" color="textSecondary">
                  due {new Date(campaign.targetDate).toLocaleDateString()}
                </Typography>
              )}
            </Box>
            <List dense>
              {campaign.checks.map(check => {
                const attestable =
                  check.kind === 'manual' &&
                  (check.status === 'pass' || check.status === 'pending');
                return (
                  <ListItem
                    key={check.checkId}
                    disableGutters
                    secondaryAction={
                      attestable ? (
                        <Checkbox
                          edge="end"
                          checked={check.status === 'pass'}
                          onChange={() =>
                            setCompletion.mutate({
                              campaignId: campaign.campaignId,
                              checkId: check.checkId,
                              componentRef: ref,
                              done: check.status !== 'pass',
                            })
                          }
                        />
                      ) : undefined
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckStatusIcon status={check.status} />
                    </ListItemIcon>
                    <ListItemText
                      primary={check.name}
                      secondary={check.kind === 'manual' ? 'Manual' : 'Automated'}
                    />
                  </ListItem>
                );
              })}
            </List>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
